import React, { useState, useEffect } from "react";
import { ContainerForm } from "./ContainerForm";
import { RuntimeComponent } from "./RuntimeComponent";
import axios from "axios";
import { ProgramContainer } from "./entities/formTemplateDataType";
import URLUtils, { getAppNameFromPath, getBxpAssetCandidates, returnUrl } from "./utilities/URLUtils";
import { RuntimeWithLayout } from "./RuntimeWithLayout";
import { RuntimeApp } from "./RuntimeApp";
import SvgSymbols from './SvgSymbols';
import SvgSecondarySymbols from './SvgSecondarySymbols';
import { configureMsalAuthority, configureMsalClientId } from "./security/msalInstance";
import { useIsAuthenticated } from "@azure/msal-react";
import { getAdminConfig } from "./services/EnvConfigService";
import * as CircularJSON from 'circular-json';
import Processing from "./commonComponent/ProcessingComponent";
import { isRuntime } from "./config";
import TrackPageView from "./utilities/TrackPageView";
import { normalizeUrl } from "./utilities/URLUtils";
import { setupAppInsights } from "./utilities/appInsightsUtils";
import SectionUtils from "./utilities/SectionUtils";
import Utils from "./utilities/Utils";
import { useHistory } from "react-router-dom";

const App: React.FC = () => {
  const [programContainer, setProgramContainer] = useState<ProgramContainer | null>(null);

  const isAuthenticated = useIsAuthenticated();

  const [processing, setProcessing] = useState(true)
  const history = useHistory();
  console.log('App entry......');
  const clearPreAuthUrl = () => {
    sessionStorage.removeItem('preAuthUrl');
    localStorage.removeItem('preAuthUrl');
  };

  const storePreAuthUrl = (url: string) => {
    sessionStorage.setItem('preAuthUrl', url);
    localStorage.setItem('preAuthUrl', url);
  };

  const isLikelyJsonResponse = (payload: string, contentType?: string | null) => {
    if (contentType?.toLowerCase().includes('json')) {
      return true;
    }

    const trimmed = payload.trim();
    if (!trimmed) {
      return false;
    }

    if (/^<!doctype html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) {
      return false;
    }

    return trimmed.startsWith('{') || trimmed.startsWith('[');
  };

  useEffect(() => {
    if (!returnUrl())
      clearPreAuthUrl()

  }, []);

  useEffect(() => {
    if (isRuntime() === 'true') {
      loadData();
    } else {
      setProcessing(false);
    }

  }, [isAuthenticated]);

  useEffect(() => {
    (window as any).programContainer = programContainer;
  }, [programContainer]);


  const loadData = async () => {
    const categoryId = getAppNameFromPath();

    if (categoryId) {

        try {
          let data;
          if (categoryId.toUpperCase().startsWith("BXP-")) {
              const formCandidates = getBxpAssetCandidates(`${categoryId.toUpperCase()}.json`);
              let formText: string | null = null;

              for (const formUrl of formCandidates) {
                console.log("Fetching form from ", formUrl);
                const res = await fetch(formUrl);
                if (!res.ok || res.status >= 300) {
                  continue;
                }

                const candidateText = await res.text();
                if (isLikelyJsonResponse(candidateText, res.headers.get('content-type'))) {
                  formText = candidateText;
                  console.log("Form fetched successfully from file ", formUrl);
                  break;
                }

                console.warn("Skipping non-JSON BXP response from ", formUrl);
              }

              if (!formText) {
                throw new Error(`BXP form not found for ${categoryId}`);
              }

              data = formText;
          }else{
            const url = "data/admin/get/" + categoryId.toLowerCase();
            const urlFull = URLUtils.concatUrls(url);
            const response = await axios.get(urlFull);
            if (Utils.isNullOrEmpty(response.data) || Utils.isNullOrEmpty(response.data.attachment)) {
              setProcessing(false);
              history.push('/on-form/404');
              return;
            }
            data = response.data.attachment;
          }

          let parsedProgramContainer = CircularJSON.parse(data) as ProgramContainer;

          SectionUtils.initProgramContainer(parsedProgramContainer);
          const msalClientIdConfigResult = configureMsalClientId(parsedProgramContainer?.UiAzureRegistration);
          if (parsedProgramContainer?.UiAzureRegistration && !msalClientIdConfigResult?.configured) {
            console.warn("[Entra ID] Invalid UI Azure Registration value. Expected Azure App Registration clientId (GUID).");
          }
          const msalAuthorityConfigResult = configureMsalAuthority(parsedProgramContainer?.UiAzureTenantId);
          if (parsedProgramContainer?.UiAzureTenantId && !msalAuthorityConfigResult?.configured) {
            console.warn("[Entra ID] Invalid UI Azure Tenant ID value. Use tenant GUID/domain or full https://login.microsoftonline.com/<tenant> authority.");
          }
          if (parsedProgramContainer?.UiAzureTenantId?.trim()) {
            sessionStorage.setItem("uiAzureTenantId", parsedProgramContainer.UiAzureTenantId.trim());
          } else {
            sessionStorage.removeItem("uiAzureTenantId");
          }

          console.log('programContainer value: ')
          console.log(parsedProgramContainer);
          const adminConfig = await getAdminConfig(parsedProgramContainer.CategoryId);
          parsedProgramContainer.adminConfig = adminConfig;
          setProgramContainer(parsedProgramContainer);
          if (localStorage.getItem("web_token")) {
            parsedProgramContainer.authToken = localStorage.getItem("web_token");
          }
          if (!parsedProgramContainer.AuthRequired) {
            clearPreAuthUrl();
          }
        } catch (ex) {
          console.log(ex);
        }
    }
    setProcessing(false);
    return null;
  };

  if (processing) {
    return <Processing />
  }

  if (programContainer?.AuthRequired && !sessionStorage.getItem("preAuthUrl") && !localStorage.getItem("preAuthUrl") && !returnUrl()) {
    storePreAuthUrl(window.location.href);
  }

  const html = [<SvgSymbols key="svg-symbols" />, <SvgSecondarySymbols key="svg-secondary-symbols" />];

  switch (isRuntime()) {
    case 'false':
      html.push(<ContainerForm key="container-form" />);
      break;
    case 'true':
      if (!programContainer) return null;
      const isEng = !window.location.href.includes("/fr/");
      const params = new URLSearchParams(window.location.search);
      if (params.size == 0 && !window.location.hash) {
        let urlString = `${window.location.origin}/on-form/${programContainer?.CategoryId}`
        if (!isEng) {
          urlString = urlString.replace("/on-form/", "/on-form/fr/");
        }
        const url = new URL(urlString);

        if (programContainer.ListOfFormSections.length > 0) {
          const sortedList = programContainer.ListOfFormSections?.sort((a, b) => a.Sequence - b.Sequence);
          let section = sortedList[0];
          let pageName = section?.pageName || section?.ENUTitle;
          pageName = pageName.trim().replace(/[^a-zA-Z0-9]/g, '-').replace(/-+$/, '');
          url.pathname = url.pathname.replace(/\/$/, '') + "/" + pageName;

        }
        
        url.search = params.toString();
        window.history.pushState({}, '', normalizeUrl(url.toString()));
      }
      
      // setupAppInsights(programContainer?.CategoryId || '');
      html.push(<RuntimeComponent key="runtime-component" programContainer={programContainer} />);
      break;
    case 'withLayout':
      setupAppInsights(programContainer?.CategoryId || '');
      html.push(<RuntimeWithLayout key="runtime-with-layout" />);
      break;
    case 'deployed':
      html.push(<RuntimeApp key="runtime-app" />);
      break;
  }

  return <>{html}</>;
};

export default App;
